import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Download, Home, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const InstallPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [appUrl, setAppUrl] = useState('');
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const url = window.location.origin;
    setAppUrl(url);

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
    setIsAndroid(/android/i.test(userAgent));

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for beforeinstallprompt event (Chrome/Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const content = {
    it: {
      title: 'Installa l\'App',
      subtitle: 'Accedi rapidamente da smartphone',
      qrTitle: 'Scansiona il QR Code',
      qrDesc: 'Inquadra questo codice con la fotocamera del tuo smartphone',
      installButton: 'Installa App',
      installedMessage: 'App già installata!',
      iosTitle: 'Istruzioni per iPhone/iPad',
      iosSteps: [
        'Tocca l\'icona Condividi in basso',
        'Scorri e tocca "Aggiungi alla schermata Home"',
        'Tocca "Aggiungi" in alto a destra',
        'L\'icona dell\'app apparirà nella Home'
      ],
      androidTitle: 'Istruzioni per Android',
      androidSteps: [
        'Tocca il pulsante "Installa App" qui sopra',
        'Oppure tocca il menu (3 punti) in alto',
        'Seleziona "Installa app"',
        'L\'icona dell\'app apparirà nella Home'
      ],
      backHome: 'Torna alla Home'
    },
    en: {
      title: 'Install the App',
      subtitle: 'Quick access from your smartphone',
      qrTitle: 'Scan the QR Code',
      qrDesc: 'Point your smartphone camera at this code',
      installButton: 'Install App',
      installedMessage: 'App already installed!',
      iosTitle: 'iPhone/iPad Instructions',
      iosSteps: [
        'Tap the Share icon at the bottom',
        'Scroll and tap "Add to Home Screen"',
        'Tap "Add" in the top right',
        'The app icon will appear on your Home'
      ],
      androidTitle: 'Android Instructions',
      androidSteps: [
        'Tap the "Install App" button above',
        'Or tap the menu (3 dots) at the top',
        'Select "Install app"',
        'The app icon will appear on your Home'
      ],
      backHome: 'Back to Home'
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      
      <div className="max-w-5xl mx-auto py-12">
        <div className="text-center mb-12">
          <Download className="w-16 h-16 mx-auto mb-4 text-primary animate-bounce" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">{t.title}</h1>
          <p className="text-lg text-muted-foreground">{t.subtitle}</p>
        </div>

        {/* Install Button - Shows on Android/Chrome when prompt is available */}
        {(deferredPrompt || isInstalled) && (
          <div className="mb-8 text-center">
            {isInstalled ? (
              <div className="inline-flex items-center gap-3 px-8 py-4 bg-green-500/20 border border-green-500/50 rounded-sm text-green-400">
                <Download className="w-6 h-6" />
                <span className="text-lg font-semibold">{t.installedMessage}</span>
              </div>
            ) : (
              <Button
                onClick={handleInstallClick}
                data-testid="install-app-button"
                className="px-8 py-6 text-lg rounded-sm border-2 border-primary bg-primary/20 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono animate-pulse"
              >
                <Download className="w-6 h-6 mr-3" />
                {t.installButton}
              </Button>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* QR Code - Hide on mobile */}
          <div className="glass-panel rounded-sm p-8 text-center border-2 border-primary/50 neon-glow hidden md:block">
            <Smartphone className="w-12 h-12 mx-auto mb-4 text-primary" />
            <h2 className="text-2xl font-semibold mb-4">{t.qrTitle}</h2>
            <p className="text-sm text-muted-foreground mb-6">{t.qrDesc}</p>
            
            {appUrl && (
              <div className="inline-block p-6 bg-white rounded-sm" data-testid="qr-code">
                <QRCodeSVG 
                  value={appUrl}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
            )}
            
            <p className="text-xs text-muted-foreground mt-4 font-mono">{appUrl}</p>
          </div>

          <div className="space-y-6">
            {/* iOS Instructions - Highlighted if on iOS */}
            <div className={`glass-panel rounded-sm p-6 border transition-all duration-300 ${
              isIOS ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : 'border-white/10'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">{t.iosTitle}</h3>
                {isIOS && <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">Il tuo dispositivo</span>}
              </div>
              
              <ol className="space-y-3">
                {t.iosSteps.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center font-mono">
                      {idx + 1}
                    </span>
                    <span className="text-sm leading-relaxed flex items-center gap-2">
                      {idx === 0 && <Share className="w-4 h-4 text-primary" />}
                      {idx === 1 && <Plus className="w-4 h-4 text-primary" />}
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Android Instructions - Highlighted if on Android */}
            <div className={`glass-panel rounded-sm p-6 border transition-all duration-300 ${
              isAndroid ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : 'border-white/10'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">{t.androidTitle}</h3>
                {isAndroid && <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">Il tuo dispositivo</span>}
              </div>
              
              <ol className="space-y-3">
                {t.androidSteps.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center font-mono">
                      {idx + 1}
                    </span>
                    <span className="text-sm leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Button
            onClick={() => navigate('/')}
            data-testid="back-home-button"
            className="rounded-sm border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono"
          >
            <Home className="w-5 h-5 mr-2" />
            {t.backHome}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallPage;
